// Utilidades compartidas para las variantes de resolucion de imagen (srcset).
//
// El backend sirve, junto a cada URL de imagen, un arreglo de variantes en distintos
// anchos (campos `image_first_srcset` / `files[].image_srcset`). Como las URLs son
// firmadas de GCS no se les puede anexar un query param en el front, asi que la unica
// forma de usar `srcset` es con las variantes que ya vienen firmadas desde el backend.
//
// La forma exacta de cada item del arreglo se lee de forma tolerante (w/width/size y
// url/image/src) para no acoplarnos a un nombre de campo puntual: si el backend cambia
// una llave menor, esto sigue funcionando.

export interface ImageVariant {
    w?: number;
    width?: number;
    size?: number;
    url?: string;
    image?: string;
    src?: string;
    [key: string]: unknown;
}

const variantWidth = (v: ImageVariant): number | undefined => {
    const raw = v?.w ?? v?.width ?? v?.size;
    const n = typeof raw === "string" ? parseInt(raw, 10) : raw;
    return typeof n === "number" && !isNaN(n) && n > 0 ? n : undefined;
};

const variantUrl = (v: ImageVariant): string | undefined => {
    const u = v?.url ?? v?.image ?? v?.src;
    return typeof u === "string" && u.length > 0 ? u : undefined;
};

// Las URLs vienen firmadas (y con hashes distintos segun el endpoint), asi que para
// asociar una imagen con sus variantes comparamos solo el nombre del archivo sin la
// firma ni el hash. Debe mantenerse identico al dedup del carousel de la tarjeta.
export const imageKey = (url: string): string => {
    try {
        const fileName = url.split("?")[0].split("/").pop() ?? url;
        return fileName
            .replace(/^cars-/, "")
            .replace(/^\d+-/, "")
            .replace(/-\d+$/, "");
    } catch (error) {
        return url;
    }
};

// Construye el string de `srcset` ("url 480w, url 1024w, ...") a partir de las variantes.
// Devuelve undefined si no hay variantes validas, para que el <img> caiga al `src` normal.
export const buildSrcSet = (variants?: ImageVariant[]): string | undefined => {
    if (!Array.isArray(variants) || variants.length === 0) return undefined;
    const parts = variants
        .map((v) => ({ w: variantWidth(v), url: variantUrl(v) }))
        .filter((x): x is { w: number; url: string } => x.w !== undefined && x.url !== undefined)
        .sort((a, b) => a.w - b.w)
        .map((x) => `${x.url} ${Math.round(x.w)}w`);
    return parts.length > 0 ? parts.join(", ") : undefined;
};

// Busca las variantes de una URL de imagen dentro del mapa que arma el DTO (keyed por
// nombre de archivo normalizado). Robusto a reordenamientos/dedup del carousel.
export const variantsFor = (
    map: Record<string, ImageVariant[]> | undefined,
    url?: string,
): ImageVariant[] | undefined => {
    if (!map || !url) return undefined;
    return map[imageKey(url)];
};

// Mayor ancho disponible entre las variantes (px). Util para decidir hasta cuanto tiene
// sentido ampliar sin pixelar.
export const maxVariantWidth = (variants?: ImageVariant[]): number | undefined => {
    if (!Array.isArray(variants) || variants.length === 0) return undefined;
    const widths = variants.map(variantWidth).filter((w): w is number => w !== undefined);
    return widths.length > 0 ? Math.max(...widths) : undefined;
};
